const countTotalPages = (imgUrlObj) =>
    imgUrlObj.reduce((sum, doc) => sum + (doc.imageUrl?.length || 0), 0);

const noop = (imgUrlObj) => ({
    ok: true,
    next: imgUrlObj,
    deletedDocIds: [],
});

const fail = (imgUrlObj, reason) => ({
    ok: false,
    next: imgUrlObj,
    deletedDocIds: [],
    reason,
});

const checkInvariant = (before, next, label) => {
    const after = countTotalPages(next);
    if (before !== after) {
        return `${label} failed: ${before} pages before, ${after} after. Please refresh.`;
    }
    return null;
};

export const applyMerge = (imgUrlObj, selectedDocs, lockedDocIds = []) => {
    const lockedSet = new Set(lockedDocIds);
    const mergeable = selectedDocs.filter((d) => !lockedSet.has(d.docId));

    if (mergeable.length < 2) return noop(imgUrlObj);

    const [keep, ...others] = mergeable;
    const otherIds = new Set(others.map((d) => d.uniqueId));
    const allPages = mergeable.flatMap((d) => d.imageUrl || []);
    const allTags = Array.from(new Set(mergeable.flatMap((d) => d.tags || [])));

    const stillLoading = allPages.some((p) => p.base64 === null);
    const mergedUniqueId = Math.random();

    const next = imgUrlObj
        .filter((doc) => !otherIds.has(doc.uniqueId))
        .map((doc) =>
            doc.uniqueId === keep.uniqueId
                ? {
                      ...doc,
                      docId: keep.docId,
                      docName: keep.docName,
                      parentId: keep.parentId,
                      imageUrl: allPages,
                      tags: allTags,
                      tagsDirty: true,
                      uniqueId: mergedUniqueId,
                      loading: stillLoading,
                  }
                : doc
        );

    const before = countTotalPages(imgUrlObj);
    const reason = checkInvariant(before, next, 'Merge');
    if (reason) return fail(imgUrlObj, reason);

    return {
        ok: true,
        next,
        deletedDocIds: others.map((d) => d.docId),
        mergedUniqueId,
    };
};

export const applySplit = (imgUrlObj, docSecId, afterPageIndex) => {
    const idx = Number(docSecId);
    const original = imgUrlObj[idx];
    if (!original) {
        return fail(imgUrlObj, `Split target index ${docSecId} not found`);
    }

    const splitAt = Number(afterPageIndex) + 1;
    const keepPages = original.imageUrl.slice(0, splitAt);
    const splitPages = original.imageUrl.slice(splitAt);

    if (splitPages.length === 0) {
        return fail(imgUrlObj, 'Cannot split: no pages after the cut point');
    }

    const effectiveRenderDocId = original.renderDocId || original.docId;
    const effectiveRenderPageStart = original.renderPageStart || 0;

    const next = imgUrlObj.map((doc, i) =>
        i === idx ? { ...doc, imageUrl: keepPages } : doc
    );
    next.push({
        docId: 'upload',
        imageUrl: splitPages,
        docName: original.docName,
        parentId: original.parentId,
        uniqueId: Math.random(),
        renderDocId: effectiveRenderDocId,
        renderPageStart: effectiveRenderPageStart + splitAt,
    });

    const before = countTotalPages(imgUrlObj);
    const reason = checkInvariant(before, next, 'Split');
    if (reason) return fail(imgUrlObj, reason);

    return { ok: true, next, deletedDocIds: [] };
};

export const applyBreak = (
    imgUrlObj,
    selectedDocs,
    breakValue,
    lockedDocIds = []
) => {
    const size = Number(breakValue);
    if (!Number.isFinite(size) || size <= 0) {
        return fail(imgUrlObj, 'Break size must be a positive number');
    }

    const lockedSet = new Set(lockedDocIds);
    const targets = selectedDocs.filter((d) => !lockedSet.has(d.docId));
    if (targets.length === 0) return noop(imgUrlObj);

    const targetIds = new Set(targets.map((d) => d.uniqueId));
    const remaining = imgUrlObj.filter((doc) => !targetIds.has(doc.uniqueId));

    const chunks = [];
    targets.forEach((data) => {
        const effectiveRenderDocId = data.renderDocId || data.docId;
        const effectiveRenderPageStart = data.renderPageStart || 0;
        const pages = data.imageUrl || [];
        for (let i = 0; i < pages.length; i += size) {
            chunks.push({
                docId: i === 0 ? data.docId : 'upload',
                imageUrl: pages.slice(i, i + size),
                docName: data.docName,
                parentId: data.parentId,
                uniqueId: Math.random(),
                renderDocId: effectiveRenderDocId,
                renderPageStart: effectiveRenderPageStart + i,
            });
        }
    });

    const next = [...remaining, ...chunks];

    const before = countTotalPages(imgUrlObj);
    const reason = checkInvariant(before, next, 'Break');
    if (reason) return fail(imgUrlObj, reason);

    return { ok: true, next, deletedDocIds: [] };
};
